// Cross-subdomain single sign-on (Aug 2026).
//
// app.studyradar.ai and lti.studyradar.ai are different origins, so they do
// NOT share localStorage — a login on the main app was invisible to the LTI
// surface and users were asked to sign in again (e.g. when a Moodle course
// links to an LTI case). Both are *.studyradar.ai though, so a cookie set on
// the parent domain is readable by every subdomain. Every user-side login
// writes the session here; any subdomain that boots without a local session
// adopts it (see src/index.js).
//
// Security note: the JWT already lives in localStorage (JS-readable), so a
// JS-readable cookie on the same trust boundary does not widen exposure.

const SSO_COOKIE = "sr_sso";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // matches access-token life

// Only share across real studyradar.ai subdomains — never on localhost/dev.
const cookieDomain = () => {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return host === "studyradar.ai" || host.endsWith(".studyradar.ai")
    ? ".studyradar.ai"
    : null;
};

export const writeSsoCookie = () => {
  const domain = cookieDomain();
  if (!domain) return;
  const access = localStorage.getItem("user_token");
  if (!access) return;
  const payload = encodeURIComponent(
    JSON.stringify({
      a: access,
      r: localStorage.getItem("user_refresh_token") || undefined,
      o: localStorage.getItem("auth_role") || "user",
    })
  );
  document.cookie = `${SSO_COOKIE}=${payload}; Domain=${domain}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Secure; SameSite=Lax`;
};

export const clearSsoCookie = () => {
  const domain = cookieDomain();
  if (!domain) return;
  document.cookie = `${SSO_COOKIE}=; Domain=${domain}; Path=/; Max-Age=0; Secure; SameSite=Lax`;
};

const readSsoCookie = () => {
  const match = document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${SSO_COOKIE}=`));
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match.slice(SSO_COOKIE.length + 1)));
    return parsed?.a ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Run before the app mounts (src/index.js). If this subdomain has no local
 * session but a sibling subdomain left one in the SSO cookie, adopt it so the
 * visitor is recognized without re-entering credentials. If a local session
 * exists, refresh the cookie instead so siblings stay in sync.
 *
 * @returns {boolean} true when a session was adopted from the cookie
 */
export const adoptSsoCookieIfNeeded = () => {
  if (!cookieDomain()) return false;
  if (localStorage.getItem("user_token")) {
    writeSsoCookie();
    return false;
  }
  const session = readSsoCookie();
  if (!session) return false;

  localStorage.setItem("user_token", session.a);
  if (session.r) localStorage.setItem("user_refresh_token", session.r);
  localStorage.setItem("auth_role", session.o || "user");
  // user_data is intentionally NOT carried in the cookie (4KB limit) — the
  // guards fetch /users/getuser as soon as a token without data is present.
  return true;
};
